"use client";

import { type RefObject, useRef } from "react";

import {
	DismissButton,
	Overlay,
	usePopover,
	useOverlayTrigger
} from "react-aria";
import {
	type OverlayTriggerState,
	useOverlayTriggerState
} from "react-stately";

import { Dots, Icon, Notification, Text } from "@pamperedchef/design-system";
import * as styles from "./AddToListDropdown.module.scss";

export type AddToListDropdownKind = "wishlist" | "registry";

export type AddToListDropdownStatus = "loading" | "ready" | "error";

export interface AddToListDropdownItem {
	pk: string;
	name: string;
}

export interface AddToListDropdownProps {
	isOpen: boolean;
	onOpenChange: (next: boolean) => void;
	status: AddToListDropdownStatus;
	wishlists: AddToListDropdownItem[];
	registries: AddToListDropdownItem[];
	onSelect: (pk: string) => void;
	onCreateNew: (kind: AddToListDropdownKind) => void;
	submittingPk?: string;
	addedPk?: string;
	addedListName?: string;
	viewListHref?: string;
	errorMessage?: string;
	triggerLabel?: string;
}

export function AddToListDropdown({
	isOpen,
	onOpenChange,
	status,
	wishlists,
	registries,
	onSelect,
	onCreateNew,
	submittingPk,
	addedPk,
	errorMessage,
	triggerLabel = "Add to Wishlist or Registry"
}: AddToListDropdownProps) {
	const triggerRef = useRef<HTMLButtonElement>(null);
	const state = useOverlayTriggerState({ isOpen, onOpenChange });
	const { triggerProps, overlayProps } = useOverlayTrigger(
		{ type: "menu" },
		state,
		triggerRef
	);

	return (
		<div
			className={styles["add-to-list-dropdown"]}
			data-component="AddToListDropdown"
		>
			<button
				type="button"
				ref={triggerRef}
				className={styles["trigger"]}
				onClick={() => state.toggle()}
				{...triggerProps}
			>
				<span className={styles["trigger__icon"]}>
					<Icon
						name="heart"
						width={20}
						height={20}
						color="currentColor"
					/>
				</span>
				<Text
					tag="span"
					styledAs="mdb"
					className={styles["trigger__label"]}
				>
					{triggerLabel}
				</Text>
				<span
					className={[
						styles["trigger__chevron"],
						state.isOpen ? styles["trigger__chevron--open"] : ""
					]
						.filter(Boolean)
						.join(" ")}
				>
					<Icon
						name="chevron-down"
						width={18}
						height={18}
						color="currentColor"
					/>
				</span>
			</button>

			{state.isOpen ? (
				<ListPopover
					state={state}
					triggerRef={triggerRef}
					overlayProps={overlayProps}
				>
					<div className={styles["menu"]}>
						{status === "loading" ? (
							<div className={styles["center"]}>
								<Dots />
								<Text tag="p" styledAs="smr">
									Loading your lists…
								</Text>
							</div>
						) : null}

						{status === "error" ? (
							<Notification
								kind="error"
								content={
									errorMessage ?? "Something went wrong."
								}
								cta={{ link: "", title: "" }}
							/>
						) : null}

						{status === "ready" ? (
							<>
								{errorMessage ? (
									<Notification
										kind="error"
										content={errorMessage}
										cta={{ link: "", title: "" }}
									/>
								) : null}

								<Section
									title="Wish Lists"
									kind="wishlist"
									createLabel="New Wish List"
									items={wishlists}
									onSelect={onSelect}
									onCreateNew={onCreateNew}
									submittingPk={submittingPk}
									addedPk={addedPk}
								/>
								<Section
									title="Registries"
									kind="registry"
									createLabel="New Registry"
									items={registries}
									onSelect={onSelect}
									onCreateNew={onCreateNew}
									submittingPk={submittingPk}
									addedPk={addedPk}
								/>
							</>
						) : null}
					</div>
				</ListPopover>
			) : null}
		</div>
	);
}

function ListPopover({
	state,
	triggerRef,
	overlayProps,
	children
}: {
	state: OverlayTriggerState;
	triggerRef: RefObject<HTMLButtonElement | null>;
	overlayProps: React.HTMLAttributes<HTMLElement>;
	children: React.ReactNode;
}) {
	const popoverRef = useRef<HTMLDivElement>(null);
	const { popoverProps, underlayProps } = usePopover(
		{
			popoverRef,
			triggerRef,
			placement: "bottom start",
			offset: 8
		},
		state
	);

	return (
		<Overlay>
			<div {...underlayProps} className={styles["underlay"]} />
			<div
				{...popoverProps}
				{...overlayProps}
				ref={popoverRef}
				className={styles["popover"]}
			>
				<DismissButton onDismiss={state.close} />
				{children}
				<DismissButton onDismiss={state.close} />
			</div>
		</Overlay>
	);
}

function Section({
	title,
	kind,
	createLabel,
	items,
	onSelect,
	onCreateNew,
	submittingPk,
	addedPk
}: {
	title: string;
	kind: AddToListDropdownKind;
	createLabel: string;
	items: AddToListDropdownItem[];
	onSelect: (pk: string) => void;
	onCreateNew: (kind: AddToListDropdownKind) => void;
	submittingPk?: string;
	addedPk?: string;
}) {
	return (
		<div className={styles["section"]}>
			<Text tag="p" className={styles["section__header"]}>
				{title}
			</Text>

			{items.map((item) => {
				const submitting = submittingPk === item.pk;
				const added = addedPk === item.pk;
				return (
					<button
						key={item.pk}
						type="button"
						className={styles["item"]}
						onClick={() => onSelect(item.pk)}
						disabled={submitting || added}
						aria-label={
							added
								? `${item.name}, added`
								: `Add to ${item.name}`
						}
					>
						<Text
							tag="span"
							styledAs="mdr"
							className={styles["item__name"]}
						>
							{item.name}
						</Text>
						{submitting ? (
							<span className={styles["item__indicator"]}>
								<Dots size={6} />
							</span>
						) : added ? (
							<span className={styles["item__indicator"]}>
								<Icon
									name="check"
									width={18}
									height={18}
									color="currentColor"
								/>
							</span>
						) : null}
					</button>
				);
			})}

			<button
				type="button"
				className={styles["create"]}
				onClick={() => onCreateNew(kind)}
			>
				<span className={styles["create__icon"]}>
					<Icon
						name="plus"
						width={20}
						height={20}
						color="currentColor"
					/>
				</span>
				<Text
					tag="span"
					styledAs="mdb"
					className={styles["create__label"]}
				>
					{createLabel}
				</Text>
			</button>
		</div>
	);
}
